/**
 * NewAgreementPage (TMMPDM-0036; the TMMPDM-0021 dialog's form): `/partners/:resellerId/agreement/new`
 * starts a reseller agreement version. The reseller and its agreement are read fresh; the form is
 * prefilled from `current` when there is one. Save validates, then the consequence confirm (it
 * ends the current agreement and re-prices every tenant of the reseller) before the POST; it returns
 * to the Agreement tab with a toast. Optional fields are omitted when blank; Connect tenant only
 * when method ≠ MANUAL. The money fields preview in plain language (no "minor units").
 * TMMPDM-0025 (tmm-api 0084): a version takes effect when it is created — no effective-from field,
 * and `effectiveFrom` is never sent. A defensive 400 on `fields.effectiveFrom` shows in the alert.
 * TMMPDM-0037 (tmm-api 0099/0100): no Revenue share field. **Official launch** is a month select (the
 * 1st, future months only, UTC); its
 * empty choice keeps the current launch (the field is omitted — FRM-007, no consequential default).
 * Once the current version has a launch there is no way to clear it; a 400 on
 * `fields.officialLaunchOn` shows on the field.
 * TMMPDM-0089 (tmm-api 0154): the agreement no longer selects a pricing card (a reseller's wholesale is
 * the wholesale side of the rate card it adopts). It carries the minimum's `currency` instead,
 * prefilled from the current one after. A save that answers after the user left doesn't move them,
 * and the return replaces the editor in history.
 * TMMPDM-0090 (tmm-api 0155): the currency is optional on the first agreement too and is sent only
 * when chosen; absent, the server takes the adopted card's (400 on `currency` with no adoption).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { useReturnTo } from '@/hooks/useReturnTo';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  ConfirmDialog,
  ErrorState,
  FormField,
  FormPage,
  FormSection,
  PageHeader,
  useNotify,
  type ErrorSummaryItem,
} from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import {
  PAYOUT_CADENCES,
  PAYOUT_METHODS,
  TAX_DOCS_STATUSES,
  type PayoutCadence,
  type PayoutMethod,
  type ResellerAgreementView,
  type ResellerAgreementWriteRequest,
  type ResellerDto,
  type TaxDocsStatus,
  type TenantView,
} from '@/api/tmm/types';
import { formatMoney, formatMonthYear, majorFromMinor, majorToMinor } from '@/format';
import { CURRENCIES } from '../rateCards/rateCardInputs';
import { launchMonthOptions } from './launchMonths';
import { TaxIdChip } from './TaxIdChip';

const METHOD_FALLBACK: Record<PayoutMethod, string> = {
  MANUAL: 'Manual',
  STRIPE_CONNECT_MANUAL: 'Stripe Connect (manual payouts)',
  STRIPE_CONNECT: 'Stripe Connect (automatic payouts)',
};

const CADENCE_FALLBACK: Record<PayoutCadence, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

const TAX_FALLBACK: Record<TaxDocsStatus, string> = {
  NOT_REQUIRED: 'Not required',
  PENDING: 'Pending',
  COLLECTED: 'Collected',
};

/** Field widths (FRM-006): selects hold their longest option; amounts and counts are short. */
const SELECT_WIDTH = '40ch';
const VALUE_WIDTH = '28ch';

/** Form order: the error summary lists fields as they appear. */
const FIELD_ORDER = [
  'currency',
  'officialLaunchOn',
  'payoutCadence',
  'payoutMethod',
  'connectTenantSlug',
  'minPayoutMinor',
  'taxDocsStatus',
  'monthlyMinimumMinor',
  'probationMonths',
  'notes',
] as const;

const fieldId = (key: string) => `agreement-${key}`;

const agreementTab = (resellerId: number) => `/partners/${resellerId}?tab=agreement`;

export function NewAgreementPage() {
  const { resellerId } = useParams();
  return <NewAgreementContent key={resellerId} resellerId={Number(resellerId)} />;
}

function NewAgreementContent({ resellerId }: { resellerId: number }) {
  const { t } = useCopy();
  const validId = Number.isInteger(resellerId) && resellerId > 0;
  const [loaded, setLoaded] = useState<{
    reseller: ResellerDto;
    current: ResellerAgreementView | null;
  } | null>(null);
  const [loadFailure, setLoadFailure] = useState<unknown>(
    validId ? null : new ApiError('UNKNOWN', 0),
  );
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!validId) return;
    let cancelled = false;
    Promise.all([adminApi.getReseller(resellerId), adminApi.getResellerAgreement(resellerId)])
      .then(([reseller, agreement]) => {
        if (cancelled) return;
        setLoaded({ reseller, current: agreement.current });
        setLoadFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoaded(null);
        setLoadFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, validId, reloadToken]);

  // One header for every state of the page (TMM-B7): loading, failed and the form are the same
  // `PageHeader` element, so the `h1` is one element whose text changes — never replaced — and
  // the late title is announced once.
  if (loadFailure !== null) {
    const title = t('resellers.agreement.loadError', 'Could not load the agreement.');
    return (
      <Box>
        <PageHeader
          title={title}
          breadcrumbs={[
            { label: t('page.partners.title', 'Partners'), to: '/partners' },
            { label: title },
          ]}
        />
        <ErrorState
          error={loadFailure}
          message={title}
          {...(validId
            ? {
                onRetry: () => {
                  setLoadFailure(null);
                  setReloadToken((n) => n + 1);
                },
              }
            : {})}
        />
      </Box>
    );
  }

  if (loaded === null) {
    const title = t('resellers.tab.agreement', 'Agreement');
    return (
      <Box>
        <PageHeader
          title={title}
          subtitle={t('common.loadingPage', 'Loading…')}
          breadcrumbs={[
            { label: t('page.partners.title', 'Partners'), to: '/partners' },
            { label: title },
          ]}
        />
        <LoadingSpinner />
      </Box>
    );
  }

  const title = loaded.current
    ? t('resellers.agreement.dialog.newTitle', 'New agreement version')
    : t('resellers.agreement.dialog.createTitle', 'Create agreement');
  return (
    <Box>
      <PageHeader
        title={title}
        breadcrumbs={[
          { label: t('page.partners.title', 'Partners'), to: '/partners' },
          { label: loaded.reseller.name, to: `/partners/${resellerId}` },
          { label: t('resellers.tab.agreement', 'Agreement'), to: agreementTab(resellerId) },
          { label: title },
        ]}
      />
      <NewAgreementForm
        resellerId={resellerId}
        resellerName={loaded.reseller.name}
        current={loaded.current}
      />
    </Box>
  );
}

function NewAgreementForm({
  resellerId,
  resellerName,
  current,
}: {
  resellerId: number;
  resellerName: string;
  current: ResellerAgreementView | null;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const notify = useNotify();
  const { backTo } = useReturnTo();
  const [tenants, setTenants] = useState<TenantView[]>([]);
  const [currencyChoice, setCurrencyChoice] = useState<string>(current?.currency ?? '');
  const currentLaunch = current?.officialLaunchOn;
  // '' keeps the current launch (the field is omitted); otherwise the 1st of a future month.
  const [launch, setLaunch] = useState('');
  const launchOptions = useMemo(
    () => launchMonthOptions().filter((month) => month !== currentLaunch),
    [currentLaunch],
  );
  const [cadence, setCadence] = useState<PayoutCadence>(current?.payoutCadence ?? 'MONTHLY');
  const [minPayout, setMinPayout] = useState(
    current ? majorFromMinor(current.minPayoutMinor, current.currency) : '',
  );
  const [monthlyMinimum, setMonthlyMinimum] = useState(
    current ? majorFromMinor(current.monthlyMinimumMinor, current.currency) : '',
  );
  const [probationMonths, setProbationMonths] = useState(
    current ? String(current.probationMonths) : '0',
  );
  const [method, setMethod] = useState<PayoutMethod>(current?.payoutMethod ?? 'MANUAL');
  const [taxDocs, setTaxDocs] = useState<TaxDocsStatus>(current?.taxDocsStatus ?? 'NOT_REQUIRED');
  const [connectTenantSlug, setConnectTenantSlug] = useState(current?.connectTenantSlug ?? '');
  const [notes, setNotes] = useState(current?.notes ?? '');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const [formError, setFormError] = useState<string | null>(null);
  // A refusal the form can't show on a field is said inside the confirm, which stays open (TMM-B4).
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState(0);

  const snapshot = JSON.stringify([
    currencyChoice,
    launch,
    cadence,
    minPayout,
    monthlyMinimum,
    probationMonths,
    method,
    taxDocs,
    connectTenantSlug,
    notes,
  ]);
  const [initialSnapshot] = useState(snapshot);
  const guard = useUnsavedChangesGuard(snapshot !== initialSnapshot, resellerName);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listTenants({ resellerId, size: 200 })
      .then((list) => {
        if (!cancelled) setTenants(list.tenants);
      })
      .catch(() => {
        if (!cancelled) setTenants([]);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId]);

  // The amounts' currency when none is chosen is the server's to decide; until one is known the
  // previews say nothing. Every currency on the list has two decimals, so the conversion is the same.
  const previewCurrency = currencyChoice === '' ? current?.currency : currencyChoice;
  const currency = previewCurrency ?? 'USD';

  /** The amount the field will send, in words (TXT-007: never "minor units"). */
  const moneyPreview = (value: string, caption: (amount: string) => string) => {
    if (value.trim() === '' || !Number.isFinite(Number(value)) || previewCurrency === undefined) {
      return undefined;
    }
    return caption(formatMoney(majorToMinor(value, currency), currency, locale));
  };

  const labels: Record<(typeof FIELD_ORDER)[number], string> = {
    currency: t('resellers.agreement.dialog.currency', 'Currency'),
    officialLaunchOn: t('resellers.agreement.dialog.officialLaunch', 'Official launch'),
    payoutCadence: t('resellers.agreement.cadenceLabel', 'Payout cadence'),
    payoutMethod: t('resellers.agreement.methodLabel', 'Payout method'),
    connectTenantSlug: t('resellers.agreement.dialog.connectTenant', 'Connect tenant'),
    minPayoutMinor: t('resellers.agreement.dialog.minPayout', 'Minimum payout'),
    taxDocsStatus: t('resellers.agreement.taxDocsLabel', 'Tax documents'),
    monthlyMinimumMinor: t('resellers.agreement.dialog.monthlyMinimum', 'Monthly minimum'),
    probationMonths: t('resellers.agreement.dialog.probation', 'Probation months'),
    notes: t('resellers.agreement.dialog.notes', 'Notes'),
  };

  const buildBody = (): ResellerAgreementWriteRequest | null => {
    const probation = Number(probationMonths);
    if (!Number.isInteger(probation) || probation < 0) return null;
    const body: ResellerAgreementWriteRequest = {
      payoutCadence: cadence,
      payoutMethod: method,
      taxDocsStatus: taxDocs,
      probationMonths: probation,
    };
    if (currencyChoice !== '') body.currency = currencyChoice;
    if (minPayout.trim() !== '') body.minPayoutMinor = majorToMinor(minPayout, currency);
    if (monthlyMinimum.trim() !== '') {
      body.monthlyMinimumMinor = majorToMinor(monthlyMinimum, currency);
    }
    if (method !== 'MANUAL' && connectTenantSlug.trim() !== '') {
      body.connectTenantSlug = connectTenantSlug.trim();
    }
    if (notes.trim() !== '') body.notes = notes.trim();
    if (launch !== '') body.officialLaunchOn = launch;
    return body;
  };

  const showErrors = (next: Record<string, string>) => {
    setFieldErrors(next);
    if (FIELD_ORDER.some((key) => next[key])) setAttempt((n) => n + 1);
  };

  const start = async () => {
    const body = buildBody();
    if (!body || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFormError(null);
    setConfirmError(null);
    setFieldErrors({});
    try {
      await adminApi.startResellerAgreement(resellerId, body);
      notify.success(
        t('resellers.agreement.started', 'Agreement version started for {{reseller}}.', {
          reseller: resellerName,
        }),
      );
      guard.leaveAfterSave(agreementTab(resellerId));
    } catch (err) {
      if (err instanceof ApiError && isValidationFailed(err)) {
        const fields = validationFields(err, t);
        // An absent currency is refused only when the reseller has adopted no card.
        if (fields.currency !== undefined && currencyChoice === '') {
          fields.currency = t(
            'resellers.agreement.currencyNoAdoption',
            "This reseller hasn't adopted a rate card, so choose the currency.",
          );
        }
        showErrors(fields);
        // No field renders effectiveFrom any more, so its message goes to the page's alert. An
        // officialLaunchOn or currency message shows on its own field.
        setFormError(
          fields.effectiveFrom ??
            (fields.officialLaunchOn !== undefined || fields.currency !== undefined
              ? null
              : errorCopy(
                  err,
                  t,
                  t('resellers.agreement.error', 'Could not start the agreement.'),
                )),
        );
        setConfirming(false);
      } else {
        setConfirmError(
          errorCopy(err, t, t('resellers.agreement.error', 'Could not start the agreement.')),
        );
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  /** Save: client checks, then the consequence confirm before anything is sent. */
  const advance = () => {
    setFormError(null);
    const next: Record<string, string> = {};
    const probation = Number(probationMonths);
    if (!Number.isInteger(probation) || probation < 0) {
      next.probationMonths = t('resellers.agreement.probationInvalid', 'Must be zero or greater.');
    }
    showErrors(next);
    if (Object.keys(next).length > 0) return;
    setConfirmError(null);
    setConfirming(true);
  };

  const title = current
    ? t('resellers.agreement.dialog.newTitle', 'New agreement version')
    : t('resellers.agreement.dialog.createTitle', 'Create agreement');
  const summary: ErrorSummaryItem[] = FIELD_ORDER.flatMap((key) => {
    const message = fieldErrors[key];
    return message ? [{ id: fieldId(key), label: labels[key], message }] : [];
  });

  return (
    <FormPage
      headerless
      title={title}
      onSubmit={advance}
      busy={submitting}
      submitLabel={
        current
          ? t('resellers.agreement.page.startVersion', 'Start new version')
          : t('resellers.agreement.page.create', 'Create agreement')
      }
      onCancel={() => void navigate(backTo(agreementTab(resellerId)))}
      guard={guard}
      errorSummary={{ items: summary, attempt }}
      formError={formError ? <Alert severity="error">{formError}</Alert> : null}
    >
      <FormSection title={t('resellers.agreement.section.pricing', 'Pricing and launch')}>
        <FormField width={VALUE_WIDTH}>
          <TextField
            id={fieldId('currency')}
            select
            label={labels.currency}
            value={currencyChoice}
            onChange={(event) => setCurrencyChoice(event.target.value)}
            error={Boolean(fieldErrors.currency)}
            helperText={
              fieldErrors.currency ??
              t('resellers.agreement.currencyHelp', "Defaults to the adopted card's currency.")
            }
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            fullWidth
          >
            <MenuItem value="">{t('resellers.agreement.currencyNotChosen', 'Not chosen')}</MenuItem>
            {CURRENCIES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
        </FormField>
        <FormField width={SELECT_WIDTH}>
          <TextField
            id={fieldId('officialLaunchOn')}
            select
            label={labels.officialLaunchOn}
            value={launch}
            onChange={(event) => setLaunch(event.target.value)}
            error={Boolean(fieldErrors.officialLaunchOn)}
            helperText={
              fieldErrors.officialLaunchOn ??
              (currentLaunch !== undefined
                ? t(
                    'resellers.agreement.dialog.launchSetHelp',
                    "Can't be removed once set. You can move it to another month.",
                  )
                : t(
                    'resellers.agreement.dialog.launchUnsetHelp',
                    'The 1st of the chosen month. No month is evaluated until one is set.',
                  ))
            }
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            fullWidth
          >
            <MenuItem value="">
              {currentLaunch !== undefined
                ? t('resellers.agreement.dialog.launchKeep', '{{month}} (current)', {
                    month: formatMonthYear(currentLaunch, locale),
                  })
                : t('resellers.agreement.dialog.launchNotSet', 'Not set')}
            </MenuItem>
            {launchOptions.map((month) => (
              <MenuItem key={month} value={month}>
                {formatMonthYear(month, locale)}
              </MenuItem>
            ))}
          </TextField>
        </FormField>
      </FormSection>

      <FormSection title={t('resellers.agreement.section.payouts', 'Payouts')}>
        <FormField width={SELECT_WIDTH}>
          <TextField
            id={fieldId('payoutCadence')}
            select
            label={labels.payoutCadence}
            value={cadence}
            onChange={(event) => setCadence(event.target.value as PayoutCadence)}
            fullWidth
          >
            {PAYOUT_CADENCES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`resellers.agreement.cadence.${value}`, CADENCE_FALLBACK[value])}
              </MenuItem>
            ))}
          </TextField>
        </FormField>
        <FormField width={SELECT_WIDTH}>
          <TextField
            id={fieldId('payoutMethod')}
            select
            label={labels.payoutMethod}
            value={method}
            onChange={(event) => setMethod(event.target.value as PayoutMethod)}
            fullWidth
          >
            {PAYOUT_METHODS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`payouts.method.${value}`, METHOD_FALLBACK[value])}
              </MenuItem>
            ))}
          </TextField>
        </FormField>
        {method !== 'MANUAL' ? (
          <FormField width={SELECT_WIDTH}>
            <TextField
              id={fieldId('connectTenantSlug')}
              select
              label={labels.connectTenantSlug}
              value={connectTenantSlug}
              onChange={(event) => setConnectTenantSlug(event.target.value)}
              error={Boolean(fieldErrors.connectTenantSlug)}
              helperText={fieldErrors.connectTenantSlug}
              fullWidth
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.slug}>
                  {tenant.name} ({tenant.slug})
                </MenuItem>
              ))}
            </TextField>
          </FormField>
        ) : null}
        <FormField width={VALUE_WIDTH}>
          <TextField
            id={fieldId('minPayoutMinor')}
            label={labels.minPayoutMinor}
            value={minPayout}
            onChange={(event) => setMinPayout(event.target.value)}
            helperText={
              fieldErrors.minPayoutMinor ||
              moneyPreview(minPayout, (amount) =>
                t('resellers.agreement.preview.minPayout', 'Paid out when at least {{amount}}', {
                  amount,
                }),
              )
            }
            error={Boolean(fieldErrors.minPayoutMinor)}
            fullWidth
          />
        </FormField>
        <FormField width={SELECT_WIDTH}>
          <TextField
            id={fieldId('taxDocsStatus')}
            select
            label={labels.taxDocsStatus}
            value={taxDocs}
            onChange={(event) => setTaxDocs(event.target.value as TaxDocsStatus)}
            fullWidth
          >
            {TAX_DOCS_STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`resellers.agreement.taxDocs.${value}`, TAX_FALLBACK[value])}
              </MenuItem>
            ))}
          </TextField>
          {/* TMMAPI-0161: the partner's tax ID status as sent (read-only); absent or null shows nothing. */}
          {current?.taxIdStatus != null ? (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" component="span">
                {t('resellers.agreement.taxIdCurrent', "Partner's tax ID at pay-api")}
              </Typography>
              <TaxIdChip status={current.taxIdStatus} />
            </Box>
          ) : null}
        </FormField>
      </FormSection>

      <FormSection title={t('resellers.agreement.section.minimum', 'Monthly minimum')}>
        <FormField width={VALUE_WIDTH}>
          <TextField
            id={fieldId('monthlyMinimumMinor')}
            label={labels.monthlyMinimumMinor}
            value={monthlyMinimum}
            onChange={(event) => setMonthlyMinimum(event.target.value)}
            helperText={
              fieldErrors.monthlyMinimumMinor ||
              moneyPreview(monthlyMinimum, (amount) =>
                t('resellers.agreement.preview.monthlyMinimum', 'At least {{amount}} per month', {
                  amount,
                }),
              )
            }
            error={Boolean(fieldErrors.monthlyMinimumMinor)}
            fullWidth
          />
        </FormField>
        <FormField width={VALUE_WIDTH}>
          <TextField
            id={fieldId('probationMonths')}
            label={labels.probationMonths}
            value={probationMonths}
            onChange={(event) => setProbationMonths(event.target.value)}
            error={Boolean(fieldErrors.probationMonths)}
            helperText={fieldErrors.probationMonths}
            type="number"
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
            fullWidth
          />
        </FormField>
      </FormSection>

      <FormSection title={labels.notes}>
        <TextField
          id={fieldId('notes')}
          label={labels.notes}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </FormSection>

      <ConfirmDialog
        open={confirming}
        title={title}
        consequence={
          <>
            {t(
              'resellers.agreement.dialog.confirm',
              'Starting a new version ends the current agreement and re-prices every tenant of {{reseller}}. Subscribers keep the offer they bought.',
              { reseller: resellerName },
            )}{' '}
            {t('resellers.agreement.dialog.takesEffectNow', 'It takes effect now.')}
            {launch !== ''
              ? ` ${t(
                  'resellers.agreement.dialog.launchConfirm',
                  "The official launch becomes {{month}}. It can't be removed once set.",
                  { month: formatMonthYear(launch, locale) },
                )}`
              : null}
          </>
        }
        confirmLabel={t('resellers.agreement.dialog.confirmSubmit', 'Start version')}
        busy={submitting}
        error={confirmError}
        onConfirm={() => void start()}
        onCancel={() => setConfirming(false)}
      />
    </FormPage>
  );
}
